/** 토스페이먼츠 결제창(v2) SDK — 카드 자동결제(빌링키) 등록 전용.
 *  카드번호 원본은 이 SDK가 여는 토스 자체 결제창에서만 입력되고, 우리 서버/DOM에는 절대 닿지
 *  않는다(PCI 범위 최소화). 성공 시 successUrl로 authKey·customerKey를 담아 리다이렉트한다. */

const SDK_URL = 'https://js.tosspayments.com/v2/standard';

/** 'TRANSFER' — 정기결제용 계좌이체(퀵계좌이체, 2026-09-07 추가). 토스 개발자문서 확인 —
 *  카드와 같은 SDK 메서드를 쓰되 method만 다르게 넘긴다. 가상계좌는 여기 없음(자동결제 자체가
 *  불가능한 1회성 입금 방식이라 이 SDK 흐름 대상이 아님). */
type TossBillingMethod = 'CARD' | 'TRANSFER';

interface TossPaymentInstance {
  requestBillingAuth: (options: {
    method: TossBillingMethod;
    successUrl: string;
    failUrl: string;
    customerName?: string;
    customerEmail?: string;
  }) => Promise<void>;
  requestPayment: (options: {
    method: 'CARD';
    amount: { currency: 'KRW'; value: number };
    orderId: string;
    orderName: string;
    successUrl: string;
    failUrl: string;
    customerName?: string;
    customerEmail?: string;
  }) => Promise<void>;
}

interface TossPaymentsInstance {
  payment: (options: { customerKey: string }) => TossPaymentInstance;
}

declare global {
  interface Window {
    TossPayments?: (clientKey: string) => TossPaymentsInstance;
  }
}

let sdkLoadPromise: Promise<void> | null = null;

function loadTossSdk(): Promise<void> {
  if (window.TossPayments) return Promise.resolve();
  if (!sdkLoadPromise) {
    sdkLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SDK_URL;
      script.onload = () => resolve();
      script.onerror = () => {
        sdkLoadPromise = null;
        reject(new Error('토스페이먼츠 SDK 로드에 실패했어요'));
      };
      document.head.appendChild(script);
    });
  }
  return sdkLoadPromise;
}

/** customerKey는 우리 서버 조회 없이 클라이언트에서 새로 발급 — 빌링키 소유권은 서버가 X-Comp-Id로
 *  studioId를 재조회해 묶으므로(요청 파라미터 신뢰 안 함), 여기선 그냥 무작위 고유값이면 충분하다. */
export function generateCustomerKey(): string {
  return crypto.randomUUID();
}

/** method='CARD'면 카드, method='TRANSFER'면 정기결제용 계좌이체(퀵계좌이체) 등록 결제창을 연다
 *  (2026-09-07 — 그 전까진 카드만 지원, 함수명은 하위호환으로 requestCardBillingAuth를 유지하되
 *  실제로는 두 방식 공용이라 method를 받도록 일반화). */
export async function requestBillingAuth(params: {
  method: TossBillingMethod;
  customerKey: string;
  successUrl: string;
  failUrl: string;
  customerName?: string;
  customerEmail?: string;
}) {
  const clientKey = import.meta.env.VITE_TOSS_CLIENT_KEY as string | undefined;
  if (!clientKey) {
    throw new Error('결제 연동이 아직 설정되지 않았어요');
  }
  await loadTossSdk();
  const tossPayments = window.TossPayments!(clientKey);
  const payment = tossPayments.payment({ customerKey: params.customerKey });
  await payment.requestBillingAuth({
    method: params.method,
    successUrl: params.successUrl,
    failUrl: params.failUrl,
    customerName: params.customerName,
    customerEmail: params.customerEmail,
  });
}

/** 결제수단을 등록·저장하지 않는 1회성 결제(빌링키 미사용, 2026-09-10 신설) — 토스 카드/간편결제
 *  통합결제창을 열어 그 자리에서 승인까지 끝낸다. 성공 시 successUrl로 paymentKey·orderId·amount가
 *  쿼리스트링으로 실려 돌아온다(requestBillingAuth의 authKey·customerKey와 같은 역할) — 서버가
 *  이 paymentKey를 최종 승인(POST /subscription/one-time-payment)해야 실제로 청구가 끝난다. */
export async function requestPayment(params: {
  customerKey: string;
  amount: number;
  orderId: string;
  orderName: string;
  successUrl: string;
  failUrl: string;
  customerName?: string;
  customerEmail?: string;
}) {
  const clientKey = import.meta.env.VITE_TOSS_CLIENT_KEY as string | undefined;
  if (!clientKey) {
    throw new Error('결제 연동이 아직 설정되지 않았어요');
  }
  await loadTossSdk();
  const tossPayments = window.TossPayments!(clientKey);
  const payment = tossPayments.payment({ customerKey: params.customerKey });
  await payment.requestPayment({
    method: 'CARD',
    amount: { currency: 'KRW', value: params.amount },
    orderId: params.orderId,
    orderName: params.orderName,
    successUrl: params.successUrl,
    failUrl: params.failUrl,
    customerName: params.customerName,
    customerEmail: params.customerEmail,
  });
}
